import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Tabs,
  Tab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add,
  Delete,
  CloudDownload,
  ContentCopy,
  Security,
  Psychology,
  ArrowBack
} from '@mui/icons-material';

function AWSAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [showAddPage, setShowAddPage] = useState(false);
  const [addPageTab, setAddPageTab] = useState(0);
  const [showCFTDialog, setShowCFTDialog] = useState(false);
  const [cftType, setCftType] = useState('readonly');
  const [formData, setFormData] = useState({
    accountName: '',
    roleArn: '',
    externalId: '',
    region: 'us-east-1'
  });
  const [generatedExternalId, setGeneratedExternalId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [platformConfig, setPlatformConfig] = useState({
    platformAwsAccountId: '204836358848',
    supportedRegions: []
  });
  // Edit credentials state
  const [editDialog, setEditDialog] = useState({ open: false, account: null });
  const [editForm, setEditForm] = useState({ roleArn: '', externalId: '', region: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    loadAccounts();
    loadPlatformConfig();
    generateExternalId();
  }, []);

  const loadPlatformConfig = async () => {
    try {
      const response = await fetch('/api/aws/platform-config');
      if (response.ok) {
        const config = await response.json();
        setPlatformConfig(config);
      }
    } catch (err) {
      console.error('Failed to load platform config:', err);
    }
  };

  const generateExternalId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const externalId = `startup-assist-pro-${timestamp}-${random}`;
    setGeneratedExternalId(externalId);
    setFormData(prev => ({ ...prev, externalId }));
  };

  const loadAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/aws/accounts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts);
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/aws/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setShowAddPage(false);
        setFormData({ accountName: '', roleArn: '', externalId: '', region: 'us-east-1' });
        generateExternalId();
        loadAccounts();
      } else {
        setError(data.error || 'Failed to add AWS account');
      }
    } catch (err) {
      console.error('Failed to add account:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/aws/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) loadAccounts();
    } catch (err) {
      console.error('Failed to delete account:', err);
    }
  };

  const openEditDialog = (account) => {
    setEditForm({ roleArn: account.role_arn || '', externalId: account.external_id || '', region: account.region || 'us-east-1' });
    setEditError('');
    setEditDialog({ open: true, account });
  };

  const handleEditSave = async () => {
    if (!editForm.roleArn.trim()) { setEditError('Role ARN is required'); return; }
    setEditLoading(true);
    setEditError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/aws/accounts/${editDialog.account.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ roleArn: editForm.roleArn.trim(), externalId: editForm.externalId.trim() || null, region: editForm.region })
      });
      const data = await response.json();
      if (response.ok) {
        setEditDialog({ open: false, account: null });
        loadAccounts();
      } else {
        setEditError(data.details || data.error || 'Failed to update credentials');
      }
    } catch (err) {
      setEditError('Network error. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const generateCloudFormationTemplate = (type) => {
    const platformAccountId = platformConfig.platformAwsAccountId;
    const baseTemplate = {
      AWSTemplateFormatVersion: '2010-09-09',
      Description: `IAM Role for Startup Assist Pro - ${type === 'readonly' ? 'Read-Only Access' : 'AI Services Access'}`,
      Parameters: {
        ExternalId: {
          Type: 'String',
          Description: 'External ID for cross-account role assumption',
          Default: `startup-assist-pro-${Math.random().toString(36).substr(2, 9)}`
        }
      }
    };

    if (type === 'readonly') {
      baseTemplate.Resources = {
        StartupAssistProRole: {
          Type: 'AWS::IAM::Role',
          Properties: {
            RoleName: 'StartupAssistProReadOnlyRole',
            AssumeRolePolicyDocument: {
              Version: '2012-10-17',
              Statement: [{
                Effect: 'Allow',
                Principal: { AWS: `arn:aws:iam::${platformAccountId}:root` },
                Action: 'sts:AssumeRole',
                Condition: { StringEquals: { 'sts:ExternalId': { Ref: 'ExternalId' } } }
              }]
            },
            ManagedPolicyArns: [
              'arn:aws:iam::aws:policy/ReadOnlyAccess',
              'arn:aws:iam::aws:policy/AWSSupportAccess',
              'arn:aws:iam::aws:policy/AWSConfigUserAccess',
              'arn:aws:iam::aws:policy/SecurityAudit',
              'arn:aws:iam::aws:policy/AWSCloudFormationReadOnlyAccess',
              'arn:aws:iam::aws:policy/AWSCostAndBillingReadOnlyAccess'
            ],
            Tags: [
              { Key: 'Purpose', Value: 'StartupAssistPro-ReadOnly' },
              { Key: 'CreatedBy', Value: 'StartupAssistPro-CFT' }
            ]
          }
        }
      };
      baseTemplate.Outputs = {
        RoleArn: { Description: 'ARN of the created IAM role', Value: { 'Fn::GetAtt': ['StartupAssistProRole', 'Arn'] }, Export: { Name: 'StartupAssistPro-ReadOnly-RoleArn' } },
        ExternalId: { Description: 'External ID used for role assumption', Value: { Ref: 'ExternalId' }, Export: { Name: 'StartupAssistPro-ReadOnly-ExternalId' } }
      };
    } else {
      baseTemplate.Resources = {
        StartupAssistProAIRole: {
          Type: 'AWS::IAM::Role',
          Properties: {
            RoleName: 'StartupAssistProAIServicesRole',
            AssumeRolePolicyDocument: {
              Version: '2012-10-17',
              Statement: [{
                Effect: 'Allow',
                Principal: { AWS: `arn:aws:iam::${platformAccountId}:root` },
                Action: 'sts:AssumeRole',
                Condition: { StringEquals: { 'sts:ExternalId': { Ref: 'ExternalId' } } }
              }]
            },
            Policies: [{
              PolicyName: 'StartupAssistProAIServicesPolicy',
              PolicyDocument: {
                Version: '2012-10-17',
                Statement: [
                  { Effect: 'Allow', Action: ['bedrock:*', 'sagemaker:DescribeEndpoint*', 'sagemaker:InvokeEndpoint*', 'comprehend:*', 'textract:*', 'rekognition:*', 'translate:*', 'polly:*', 'transcribe:*'], Resource: '*' },
                  { Effect: 'Allow', Action: ['logs:CreateLogGroup', 'logs:CreateLogStream', 'logs:PutLogEvents'], Resource: 'arn:aws:logs:*:*:log-group:/startup-assist-pro/*' }
                ]
              }
            }],
            Tags: [
              { Key: 'Purpose', Value: 'StartupAssistPro-AI-Services' },
              { Key: 'CreatedBy', Value: 'StartupAssistPro-CFT' }
            ]
          }
        }
      };
      baseTemplate.Outputs = {
        RoleArn: { Description: 'ARN of the created IAM role for AI services', Value: { 'Fn::GetAtt': ['StartupAssistProAIRole', 'Arn'] }, Export: { Name: 'StartupAssistPro-AI-RoleArn' } },
        ExternalId: { Description: 'External ID used for role assumption', Value: { Ref: 'ExternalId' }, Export: { Name: 'StartupAssistPro-AI-ExternalId' } }
      };
    }
    return baseTemplate;
  };

  const downloadCloudFormationTemplate = (type) => {
    const template = generateCloudFormationTemplate(type);
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `startup-assist-pro-${type}-role.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyTemplateToClipboard = (type) => {
    const template = generateCloudFormationTemplate(type);
    navigator.clipboard.writeText(JSON.stringify(template, null, 2));
  };

  const getTrustPolicyJson = () => JSON.stringify({
    Version: '2012-10-17',
    Statement: [{
      Effect: 'Allow',
      Principal: { AWS: `arn:aws:iam::${platformConfig.platformAwsAccountId}:root` },
      Action: 'sts:AssumeRole',
      Condition: { StringEquals: { 'sts:ExternalId': generatedExternalId } }
    }]
  }, null, 2);

  /* ── Add Account Page ── */
  if (showAddPage) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => setShowAddPage(false)} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Add AWS Account</Typography>
        </Box>

        <Tabs value={addPageTab} onChange={(e, v) => setAddPageTab(v)} sx={{ mb: 3 }}>
          <Tab label="CloudFormation Setup" />
          <Tab label="Manual Setup" />
          <Tab label="Connect Account" />
        </Tabs>

        {/* CloudFormation Setup Tab */}
        {addPageTab === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                Use CloudFormation to automatically create the IAM role with the correct permissions and trust policy.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              startIcon={<CloudDownload />}
              onClick={() => setShowCFTDialog(true)}
              sx={{ mb: 3 }}
            >
              Generate CloudFormation Template
            </Button>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card><CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 1: Generate Template</Typography>
                  <Typography variant="body2" paragraph>
                    Download our pre-configured CloudFormation template. It creates an IAM role with a unique External ID for secure access.
                  </Typography>
                  <Alert severity="info">
                    <Typography variant="body2">The template generates a unique External ID as an additional security layer.</Typography>
                  </Alert>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card><CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 2: Deploy in AWS Console</Typography>
                  <Typography variant="body2" paragraph>
                    Upload the template to AWS CloudFormation console and deploy it to create the IAM role.
                  </Typography>
                  <Alert severity="info">
                    <Typography variant="body2">The template creates a role with read-only permissions and secure cross-account access.</Typography>
                  </Alert>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card><CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 3: Copy Role ARN & External ID</Typography>
                  <Typography variant="body2" paragraph>
                    After deployment, copy both the Role ARN and External ID from the CloudFormation outputs tab.
                  </Typography>
                  <Alert severity="warning">
                    <Typography variant="body2">You need both the Role ARN and External ID from CloudFormation outputs to connect your account.</Typography>
                  </Alert>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card><CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 4: Connect Account</Typography>
                  <Typography variant="body2" paragraph>
                    Go to the "Connect Account" tab and enter the Role ARN and External ID from CloudFormation outputs.
                  </Typography>
                  <Button variant="outlined" startIcon={<Add />} onClick={() => setAddPageTab(2)}>
                    Go to Connect Account
                  </Button>
                </CardContent></Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Manual Setup Tab */}
        {addPageTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }}>
                <Typography variant="body2">
                  Create the IAM role manually in AWS Console instead of using CloudFormation.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card><CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 1: Get External ID</Typography>
                <Typography variant="body2" paragraph>
                  Use this External ID in the IAM role trust policy.
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {generatedExternalId}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button size="small" onClick={generateExternalId} variant="outlined">Generate New ID</Button>
                  <Button size="small" onClick={() => navigator.clipboard.writeText(generatedExternalId)} variant="outlined" startIcon={<ContentCopy />}>Copy</Button>
                </Box>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card><CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 2: Create IAM Role</Typography>
                <Typography variant="body2" paragraph>In AWS Console, go to IAM → Roles → Create Role:</Typography>
                <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
                  <li>Select "AWS Account" as trusted entity</li>
                  <li>Choose "Another AWS Account"</li>
                  <li>Enter Account ID: <strong>{platformConfig.platformAwsAccountId}</strong></li>
                  <li>Check "Require external ID"</li>
                  <li>Paste the External ID from Step 1</li>
                </Typography>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card><CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 3: Attach Policies</Typography>
                <Typography variant="body2" paragraph>Attach these AWS managed policies to the role:</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li><strong>ReadOnlyAccess</strong></li>
                  <li><strong>SecurityAudit</strong></li>
                  <li><strong>AWSSupportAccess</strong></li>
                  <li><strong>AWSConfigUserAccess</strong></li>
                  <li><strong>AWSCostAndBillingReadOnlyAccess</strong></li>
                </Typography>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card><CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Step 4: Copy Role ARN</Typography>
                <Typography variant="body2" paragraph>After creating the role, copy its ARN from the role summary page.</Typography>
                <Typography variant="body2" paragraph>Then go to "Connect Account" tab with:</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li><strong>Role ARN:</strong> From AWS Console</li>
                  <li><strong>External ID:</strong> From Step 1 above</li>
                </Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={() => setAddPageTab(2)} sx={{ mt: 2 }}>
                  Go to Connect Account
                </Button>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12}>
              <Card><CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Sample Trust Policy (for reference)</Typography>
                <Typography variant="body2" paragraph>If you need to edit the trust policy manually, here's the format:</Typography>
                <Box sx={{
                  p: 2, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto',
                  '& pre': { margin: 0, fontFamily: '"Google Sans Code", "Roboto Mono", monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                }}>
                  <pre>{getTrustPolicyJson()}</pre>
                </Box>
                <Button
                  size="small"
                  onClick={() => navigator.clipboard.writeText(getTrustPolicyJson())}
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  sx={{ mt: 1 }}
                >
                  Copy Trust Policy
                </Button>
              </CardContent></Card>
            </Grid>
          </Grid>
        )}

        {/* Connect Account Tab (form) */}
        {addPageTab === 2 && (
          <Card sx={{ maxWidth: 600 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Connect AWS Account</Typography>
              <form onSubmit={handleSubmit}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>What is External ID?</strong> It's a unique security token that prevents unauthorized access to your AWS role.
                    We've auto-generated one for you, or you can use the one from your CloudFormation deployment.
                  </Typography>
                </Alert>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <TextField
                  autoFocus margin="dense" label="Account Name" fullWidth variant="outlined"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                  required sx={{ mb: 2 }}
                  placeholder="e.g., Production Account, Development Account"
                  disabled={loading}
                />
                <TextField
                  margin="dense" label="IAM Role ARN" fullWidth variant="outlined"
                  placeholder="arn:aws:iam::123456789012:role/StartupAssistProReadOnlyRole"
                  value={formData.roleArn}
                  onChange={(e) => setFormData({ ...formData, roleArn: e.target.value })}
                  required sx={{ mb: 2 }}
                  helperText="Copy this from your CloudFormation stack outputs"
                  disabled={loading}
                />
                <Box sx={{ mb: 2 }}>
                  <TextField
                    margin="dense" label="External ID" fullWidth variant="outlined"
                    value={formData.externalId}
                    onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                    required
                    helperText="Use the External ID from CloudFormation outputs, or keep the auto-generated one"
                    disabled={loading}
                  />
                  <Box display="flex" gap={1} mt={1}>
                    <Button size="small" onClick={generateExternalId} variant="outlined" disabled={loading}>Generate New ID</Button>
                    <Button size="small" onClick={() => navigator.clipboard.writeText(formData.externalId)} variant="outlined" startIcon={<ContentCopy />} disabled={loading}>Copy ID</Button>
                  </Box>
                </Box>
                <FormControl fullWidth disabled={loading} sx={{ mb: 3 }}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    label="Region"
                  >
                    {platformConfig.supportedRegions.map((region) => (
                      <MenuItem key={region.value} value={region.value}>{region.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box display="flex" gap={2}>
                  <Button onClick={() => setShowAddPage(false)} disabled={loading}>Cancel</Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? 'Adding Account...' : 'Add Account'}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        )}

        {/* CloudFormation Template Dialog */}
        <Dialog open={showCFTDialog} onClose={() => setShowCFTDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={2}>
              <CloudDownload color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Generate CloudFormation Template</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph>Choose the type of access you want to grant:</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{ cursor: 'pointer', border: cftType === 'readonly' ? 2 : 1, borderColor: cftType === 'readonly' ? 'primary.main' : 'divider' }}
                  onClick={() => setCftType('readonly')}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Security color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Read-Only Access</Typography>
                    </Box>
                    <Typography variant="body2" paragraph>
                      Grants read-only access for CSPM scanning, cost analysis, and compliance monitoring.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Includes:</Typography>
                    <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                      <li>ReadOnlyAccess</li>
                      <li>SecurityAudit</li>
                      <li>Cost and Billing Read-Only</li>
                      <li>AWS Config User Access</li>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{ cursor: 'pointer', border: cftType === 'ai' ? 2 : 1, borderColor: cftType === 'ai' ? 'primary.main' : 'divider' }}
                  onClick={() => setCftType('ai')}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Psychology color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>AI Services Access</Typography>
                    </Box>
                    <Typography variant="body2" paragraph>
                      Additional permissions for AI-powered features using AWS Bedrock, SageMaker, and other AI services.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Includes:</Typography>
                    <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                      <li>Amazon Bedrock</li>
                      <li>SageMaker Inference</li>
                      <li>Comprehend, Textract</li>
                      <li>Rekognition, Translate</li>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="body2">
                Deploy the Read-Only template first for basic functionality. The AI Services template is optional.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowCFTDialog(false)}>Cancel</Button>
            <Button onClick={() => copyTemplateToClipboard(cftType)} startIcon={<ContentCopy />}>Copy Template</Button>
            <Button variant="contained" onClick={() => downloadCloudFormationTemplate(cftType)} startIcon={<CloudDownload />}>Download Template</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  /* ── Connected Accounts (main view) ── */
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>AWS Accounts</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setShowAddPage(true); setAddPageTab(0); }}>
          Add Account
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          <strong>Secure Access:</strong> Connect your AWS accounts using IAM roles for secure, credential-free access.
          The External ID acts as an additional security layer preventing unauthorized access to your AWS role.
        </Typography>
      </Alert>

      {accounts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Security sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No AWS accounts connected
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add your first AWS account to start using CSPM and automation features
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setShowAddPage(true); setAddPageTab(0); }}>
              Add AWS Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Connected AWS Accounts
            </Typography>
            <List>
              {accounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{account.account_name}</Typography>
                          {account.is_primary && <Chip label="Primary" color="primary" size="small" />}
                          {!account.external_id && <Chip label="No External ID" color="warning" size="small" />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">Account ID: {account.account_id}</Typography>
                          <Typography variant="body2" color="text.secondary">Region: {account.region}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            Role: {account.role_arn}
                          </Typography>
                          <Typography variant="body2" color={account.external_id ? 'text.secondary' : 'warning.main'} sx={{ fontSize: '0.75rem' }}>
                            External ID: {account.external_id || '⚠ Not set — scans may fail if trust policy requires one'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Added: {new Date(account.created_at).toLocaleDateString()}</Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Update Role Credentials">
                        <IconButton edge="end" onClick={() => openEditDialog(account)} color="primary" sx={{ mr: 1 }}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Account">
                        <IconButton edge="end" onClick={() => handleDeleteAccount(account.id)} color="error">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < accounts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Edit Credentials Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, account: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Update Role Credentials — {editDialog.account?.account_name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Update the IAM role ARN and External ID. The new credentials will be verified before saving.
          </Alert>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField
            fullWidth label="IAM Role ARN" variant="outlined" sx={{ mb: 2, mt: 1 }}
            value={editForm.roleArn}
            onChange={(e) => setEditForm({ ...editForm, roleArn: e.target.value })}
            placeholder="arn:aws:iam::123456789012:role/YourRole"
            disabled={editLoading}
          />
          <TextField
            fullWidth label="External ID (leave blank if not required)" variant="outlined" sx={{ mb: 2 }}
            value={editForm.externalId}
            onChange={(e) => setEditForm({ ...editForm, externalId: e.target.value })}
            placeholder="e.g. startup-assist-pro-abc123"
            disabled={editLoading}
            helperText="Must match the ExternalId condition in your IAM role's trust policy"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog({ open: false, account: null })} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? 'Verifying & Saving...' : 'Save Credentials'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AWSAccounts;
